"use client"

import type { ReactNode } from "react"
import { MoreHorizontal, Search } from "lucide-react"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  pipelineStageColors,
  radiusScale,
  spacingScale,
  surfaceLevels,
  typeRuleExamples,
  typographyScale,
} from "@/lib/design-tokens"
import { cn } from "@/lib/utils"

/**
 * Graphite, as actually implemented.
 *
 * Scoped deliberately to the rules that govern code: how surfaces stack, when
 * type is mono, what the palette means, and what the shell looks like. The
 * design system project carries eighteen further guideline pages (brand, motion,
 * Robin states, the full colour and spacing treatments) that are not reproduced
 * here.
 *
 * Source of truth is the canvas — `Reimagining Recruiter OS.dc.html` in the
 * Claude Design project. Where this page and the canvas disagree, the canvas
 * wins and this page is wrong.
 */

function Section({
  id,
  eyebrow,
  title,
  description,
  children,
}: {
  id: string
  eyebrow: string
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <section id={id} className="scroll-mt-20 space-y-6">
      <div className="space-y-1.5">
        <p className="font-mono text-[11px] uppercase tracking-widest text-brand-ink">
          {eyebrow}
        </p>
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
        <p className="max-w-3xl text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  )
}

function Panel({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="rounded-lg border bg-card">
      <div className="border-b px-4 py-3">
        <h3 className="text-sm font-medium">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  )
}

/** The rule shown rather than described: chrome outside, canvas between, surfaces on top. */
function SurfaceRulePreview() {
  return (
    <div className="overflow-hidden rounded-lg border bg-chrome">
      <div className="flex h-11 items-center gap-3 border-b px-3">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-[11px] font-bold text-primary-foreground">
          R
        </span>
        <span className="text-xs font-medium">Chrome — the frame</span>
      </div>
      <div className="bg-background p-4">
        <p className="mb-3 text-xs text-muted-foreground">
          Canvas — the gap. Nothing sits directly on it.
        </p>
        <div className="space-y-2">
          <div className="rounded-lg border bg-card p-3 text-xs">
            Surface — every piece of content lives on one of these
          </div>
          <div className="overflow-hidden rounded-lg border bg-card text-xs">
            <div className="border-b bg-foreground/[0.03] px-3 py-2 font-medium">
              Inside a surface, only header rows and zebra may be tinted
            </div>
            <div className="px-3 py-2">Row</div>
            <div className="bg-foreground/[0.02] px-3 py-2">Row</div>
          </div>
        </div>
      </div>
    </div>
  )
}

/** The ATS half of the shell: pinned top nav over a scrolling route container. */
function ShellPreview() {
  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="flex h-14 items-stretch gap-2.5 border-b bg-chrome px-[18px]">
        <div className="flex shrink-0 items-center gap-2.5 pr-3.5">
          <span className="flex h-[26px] w-[26px] items-center justify-center rounded-md bg-primary text-[13px] font-bold text-primary-foreground">
            R
          </span>
          <span className="hidden text-[13px] font-semibold sm:inline">
            Recruiter OS
          </span>
        </div>
        <nav className="flex min-w-0 flex-1 items-stretch gap-5 overflow-hidden">
          {["Dashboard", "Jobs", "Candidates", "Placements", "Searches"].map(
            (item, index) => (
              <span
                key={item}
                className={cn(
                  "-mb-px inline-flex shrink-0 items-center whitespace-nowrap border-b-2 border-transparent px-0.5 text-[13px]",
                  index === 0
                    ? "border-primary font-medium text-foreground"
                    : "text-muted-foreground",
                )}
              >
                {item}
              </span>
            ),
          )}
          <span className="-mb-px inline-flex items-center px-1 text-muted-foreground">
            <MoreHorizontal className="h-4 w-4" />
          </span>
        </nav>
        <div className="flex shrink-0 items-center gap-3">
          <div className="hidden h-[34px] w-[220px] items-center gap-2.5 rounded-md border bg-background px-3 text-xs text-muted-foreground xl:flex">
            <Search className="h-[15px] w-[15px]" />
            <span className="flex-1">Search or run a command</span>
            <span className="font-mono text-[10.5px]">⌘K</span>
          </div>
          <Avatar className="h-[30px] w-[30px]">
            <AvatarFallback className="bg-primary/15 text-[11px] font-medium text-brand-ink">
              SW
            </AvatarFallback>
          </Avatar>
        </div>
      </div>
      <div className="space-y-3 bg-background p-5">
        <h3 className="text-2xl font-semibold tracking-tight">Jobs</h3>
        <div className="rounded-lg border bg-card p-4 text-xs leading-relaxed text-muted-foreground">
          Route content scrolls under the pinned nav. On the real shell this whole
          column sits in a resizable panel beside the chat pane, so page layouts
          must stay legible at roughly two thirds width — which is why the nav uses
          container queries rather than viewport breakpoints.
        </div>
      </div>
    </div>
  )
}

const SEMANTIC_COLOURS = [
  {
    name: "Primary",
    cls: "bg-primary",
    hex: "#16D0BC",
    usage: "Active state — the one thing that matters",
  },
  {
    name: "Accent",
    cls: "bg-accent",
    hex: "#EFC02A",
    usage: "Needs you — pending asks, attention",
  },
  {
    name: "Destructive",
    cls: "bg-destructive",
    hex: "#F4615F",
    usage: "Irreversible actions, errors",
  },
  {
    name: "Muted",
    cls: "bg-muted-foreground",
    hex: "#8A929C",
    usage: "Secondary text, metadata",
  },
] as const

const DIVERGENCES = [
  {
    title: "The design system's own tokens are stale",
    detail:
      "tokens/colors.css in the Claude Design project still documents the pre-Graphite app, including a --shell-sidebar slate ramp for a sidebar that no longer exists. The Graphite palette lives only in the canvas artboards and was extracted from them by hand.",
  },
  {
    title: "Cards versus “no cards anywhere”",
    detail:
      "The canvas annotation for the job console says “No cards anywhere”, while the design system ships Card, CandidateCard, JobCard and MetricCard. The instrument-style screens use hairline lists and one bordered table; the rest of the product still uses cards. Unresolved.",
  },
  {
    title: "Money at rest needs a backend field",
    detail:
      "The dashboard's centrepiece ranks jobs by fee × days still, and the canvas marks fee on job as a new field. The metric cannot ship until it exists.",
  },
  {
    title: "Light mode is not designed",
    detail:
      "Graphite exists only in .dark. The app defaults to dark now, but light mode remains the pre-Graphite theme.",
  },
] as const

export function DesignSystemPage() {
  return (
    <div className="mx-auto max-w-5xl space-y-14 px-6 py-10">
      <header className="space-y-2">
        <p className="font-mono text-[11px] uppercase tracking-widest text-brand-ink">
          Graphite
        </p>
        <h1 className="text-3xl font-bold tracking-tight">Design System</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          A precision instrument: hairlines, mono numerals, restrained surfaces,
          and teal used only where it means something. This page documents the
          parts that govern code.
        </p>
      </header>

      <Section
        id="surfaces"
        eyebrow="Foundations"
        title="Surfaces"
        description="One rule, no exceptions: chrome is the frame, canvas is the gap, and every piece of content sits on a surface. Note the direction — chrome is darker than the page and surfaces are lighter, which is why a single card token could not express it. Below 640px the canvas collapses: a phone screen is itself the surface."
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <Panel title="The three levels" hint="Plus the hairline that separates them">
            <div className="space-y-2">
              {surfaceLevels.map((level) => (
                <div
                  key={level.token}
                  className="flex items-center gap-3 rounded-md border p-2"
                >
                  <div
                    className={cn(
                      "h-10 w-10 shrink-0 rounded-md border",
                      level.className,
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium">{level.name}</span>
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {level.hex}
                      </span>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {level.usage}
                    </p>
                  </div>
                  <code className="shrink-0 font-mono text-[11px] text-muted-foreground">
                    {level.token}
                  </code>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Applied" hint="Chrome outside, canvas between, surfaces on top">
            <SurfaceRulePreview />
          </Panel>
        </div>
      </Section>

      <Section
        id="type"
        eyebrow="Foundations"
        title="Type"
        description="Inter carries language, JetBrains Mono carries machine values — timestamps, counts, money readouts, record ids, keyboard shortcuts. The edge cases are what make this a rule rather than a preference."
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <Panel
            title="Language or machine?"
            hint="Including the cases that catch people out"
          >
            <div className="space-y-1.5">
              {typeRuleExamples.map((example) => (
                <div key={example.value} className="rounded-md border p-2.5">
                  <div className="flex items-baseline gap-2">
                    <span
                      className={cn(
                        "text-sm",
                        example.kind === "machine" && "font-mono",
                      )}
                    >
                      {example.value}
                    </span>
                    <Badge
                      variant={example.kind === "machine" ? "default" : "secondary"}
                      className="ml-auto shrink-0 font-mono text-[10px]"
                    >
                      {example.kind}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{example.why}</p>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Scale" hint="Inter, for everything that is language">
            <div className="space-y-3">
              {typographyScale.map((token) => (
                <div key={token.name} className="border-b pb-3 last:border-0 last:pb-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className={token.className}>{token.sample}</span>
                    <code className="shrink-0 font-mono text-[11px] text-muted-foreground">
                      {token.name}
                    </code>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{token.usage}</p>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </Section>

      <Section
        id="colour"
        eyebrow="Foundations"
        title="Colour"
        description="Neutrals do the work. Teal is the only accent that means “this is the thing”, amber means “needs you”, and the eight pipeline stage colours are preserved from the existing product."
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <Panel title="Semantic" hint="Reserved meanings — not decoration">
            <div className="grid gap-2 sm:grid-cols-2">
              {SEMANTIC_COLOURS.map((item) => (
                <div key={item.name} className="rounded-md border p-2.5">
                  <div className={cn("mb-2 h-8 rounded", item.cls)} />
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium">{item.name}</span>
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {item.hex}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{item.usage}</p>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Pipeline stages" hint="Eight stages, colours preserved">
            <div className="space-y-1.5">
              {pipelineStageColors.map((stage) => (
                <div key={stage.token} className="flex items-center gap-3">
                  <span
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: stage.hex }}
                  />
                  <span className="flex-1 text-sm">{stage.name}</span>
                  <code className="font-mono text-[11px] text-muted-foreground">
                    {stage.hex}
                  </code>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </Section>

      <Section
        id="shape"
        eyebrow="Foundations"
        title="Shape & spacing"
        description="Hairlines over shadows. Elevation is used only where something genuinely floats — the chat pane over the ATS, dialogs, the resize handle."
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <Panel title="Radius">
            <div className="space-y-2">
              {radiusScale.map((token) => (
                <div key={token.token} className="flex items-center gap-3">
                  <div
                    className={cn("h-9 w-9 shrink-0 border bg-card", token.className)}
                  />
                  <div className="min-w-0 flex-1">
                    <code className="font-mono text-[11px]">{token.token}</code>
                    <p className="truncate text-xs text-muted-foreground">
                      {token.usage}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Spacing">
            <div className="space-y-2">
              {spacingScale.map((token) => (
                <div key={token.token} className="flex items-center gap-3">
                  <div className="flex h-6 w-16 shrink-0 items-center">
                    <div
                      className="h-2 rounded-sm bg-primary/60"
                      style={{ width: token.px }}
                    />
                  </div>
                  <code className="w-20 shrink-0 font-mono text-[11px]">
                    {token.token} · {token.px}px
                  </code>
                  <p className="truncate text-xs text-muted-foreground">
                    {token.usage}
                  </p>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </Section>

      <Section
        id="shell"
        eyebrow="Layout"
        title="Shell"
        description="Routes live in a pinned top nav; the left column is the conversation rail. The ATS sits in a resizable panel beside the chat pane, and the user can drag that split anywhere between chat-heavy and ATS-only."
      >
        <ShellPreview />
      </Section>

      <Section
        id="divergences"
        eyebrow="Honesty"
        title="Where code and design disagree"
        description="Recorded deliberately, so nobody has to rediscover them."
      >
        <div className="space-y-3">
          {DIVERGENCES.map((item) => (
            <div key={item.title} className="rounded-lg border bg-card p-4">
              <h3 className="text-sm font-medium">{item.title}</h3>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {item.detail}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <footer className="flex flex-wrap items-center gap-3 border-t pt-6">
        <p className="text-xs text-muted-foreground">
          Source of truth:{" "}
          <code className="font-mono">Reimagining Recruiter OS.dc.html</code>
        </p>
        <Button asChild variant="outline" size="sm" className="ml-auto">
          <a
            href="https://claude.ai/design/p/dc087486-b16a-4ba2-a50f-fbb1089b6830"
            target="_blank"
            rel="noreferrer"
          >
            Open the canvas
          </a>
        </Button>
      </footer>
    </div>
  )
}
